import { EVENT_CSS_VARS } from '../styles/cssVariables';
import type { BorderStyle, ColorTreatment, EventVariant } from '../types';

/**
 * Creates CSS variables object for event styling based on variant and color treatment
 * This is the SINGLE SOURCE OF TRUTH for all variant-specific styling
 *
 * All variant differences (standard/actionable/block) are expressed as CSS variable values.
 * Styled components consume these variables, remaining "dumb" and highly reusable.
 *
 * @param variant - Event variant (standard/actionable/block)
 * @param colorTreatment - Color scheme from theme
 * @param borderStyle - Border style for actionable variant (solid/dashed)
 * @returns Style object with CSS variable definitions
 *
 * @example
 * const cssVars = createEventCSSVars('standard', colorTreatment);
 * <Box style={cssVars}>
 *   <StyledEventBox /> // Consumes CSS variables
 * </Box>
 */
export const createEventCSSVars = (
  variant: EventVariant,
  colorTreatment: ColorTreatment,
  borderStyle?: BorderStyle,
): Record<string, string> => {
  // Base CSS variables (shared across all variants)
  const baseVars: Record<string, string> = {
    [EVENT_CSS_VARS.color.accent]: colorTreatment.accentColor,
  };

  // Variant-specific CSS variables
  switch (variant) {
    case 'standard':
      return {
        ...baseVars,
        [EVENT_CSS_VARS.color.bg]: colorTreatment.backgroundColor,
        [EVENT_CSS_VARS.color.text]: colorTreatment.textColor,
        [EVENT_CSS_VARS.color.border]: 'transparent',
        [EVENT_CSS_VARS.outline.width]: '0px',
        [EVENT_CSS_VARS.outline.style]: 'solid',
        [EVENT_CSS_VARS.borderLeft.width]: '0px',
        [EVENT_CSS_VARS.shadow]: '0 2px 6px rgba(0, 0, 0, 0.15)',
        [EVENT_CSS_VARS.expanded.outline.width]: '1px',
        [EVENT_CSS_VARS.expanded.outline.style]: 'solid',
        [EVENT_CSS_VARS.expanded.shadow]: '0 8px 24px rgba(0, 0, 0, 0.25)',
      };

    case 'actionable':
      return {
        ...baseVars,
        [EVENT_CSS_VARS.color.bg]: '#ffffff',
        [EVENT_CSS_VARS.color.text]: '#535976',
        [EVENT_CSS_VARS.color.border]: colorTreatment.accentColor,
        [EVENT_CSS_VARS.outline.width]: '1.5px',
        [EVENT_CSS_VARS.outline.style]: borderStyle === 'dashed' ? 'dashed' : 'solid',
        [EVENT_CSS_VARS.borderLeft.width]: '12px',
        [EVENT_CSS_VARS.shadow]: '0 2px 6px rgba(0, 0, 0, 0.10)',
        [EVENT_CSS_VARS.expanded.outline.width]: '2px',
        [EVENT_CSS_VARS.expanded.outline.style]: borderStyle === 'dashed' ? 'dashed' : 'solid',
        [EVENT_CSS_VARS.expanded.shadow]: '0 12px 32px rgba(0, 0, 0, 0.3)',
      };

    case 'block':
      return {
        ...baseVars,
        [EVENT_CSS_VARS.color.bg]: '#EEF0FF',
        [EVENT_CSS_VARS.color.text]: '#535976',
        [EVENT_CSS_VARS.color.border]: '#CFD3E8',
        [EVENT_CSS_VARS.outline.width]: '1px',
        [EVENT_CSS_VARS.outline.style]: 'solid',
        [EVENT_CSS_VARS.borderLeft.width]: '1px',
        [EVENT_CSS_VARS.shadow]: '0 1px 3px rgba(0, 0, 0, 0.08)',
        [EVENT_CSS_VARS.expanded.outline.width]: '1px',
        [EVENT_CSS_VARS.expanded.outline.style]: 'solid',
        [EVENT_CSS_VARS.expanded.shadow]: '0 4px 16px rgba(0, 0, 0, 0.15)',
      };

    default:
      return baseVars;
  }
};
